import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  useGetDeminimisPagination,
  useGetFiltersForDeminimisDataGrid,
} from "../../services/Deminimis/deminimisAPI";
import { useExportCsv } from "../../services/Shared/sharedAPI";
import DataGrid from "../dataGrid/DataGrid";
import formatDateHelper, { formatDateTimeHelper } from "../utils/dateFormat";

const DeminimisNacrtovaneDataGrid = ({ onOpenDetails }) => {
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const [sorting, setSorting] = useState([]);
  const [globalSearch, setGlobalSearch] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);

  useEffect(() => {
    // Reset page index to 0 when search or sorting changes
    setPagination((old) => ({
      ...old,
      pageIndex: 0,
    }));
  }, [globalSearch, sorting, columnFilters]);

  const navigate = useNavigate();

  // Fetch paginated deminimis based on current pagination, sorting, search and filters
  const { data: deminimisData, isLoading } = useGetDeminimisPagination(
    pagination,
    sorting,
    globalSearch,
    columnFilters,
  );

  //Fetch filters (šifrants) for the data grid (dropdowns)
  const { data: filters } = useGetFiltersForDeminimisDataGrid();

  // Export CSV mutation
  const exportMutation = useExportCsv(
    pagination,
    sorting,
    globalSearch,
    columnFilters,
    "deminimis-pomoc/seznamPaginationV2",
  );

  const amountFormatter = useMemo(
    () =>
      new Intl.NumberFormat("sl-SI", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true,
      }),
    [],
  );

  const formatOptionalAmount = (value) =>
    value === null || value === undefined ? "-" : amountFormatter.format(value);

  //Set up columns for the data grid
  const columns = React.useMemo(
    () => [
      {
        header: "Prejemnik pomoči",
        accessorKey: "prejemnik",
        enableColumnFilter: true,
        meta: {
          filterVariant: "checkboxFilter",
          filterOptions: filters?.prejemniki || [],
        },
      },
      {
        header: "Dajalec pomoči",
        accessorKey: "dajalec",
      },
      {
        header: "Projekt",
        accessorKey: "projekt",
      },
      {
        header: "Datum načrtovane pomoči",
        accessorKey: "datumNacrtovanePomoci",
        enableColumnFilter: true,
        meta: {
          filterVariant: "dateRangeFilter",
        },
        cell: (info) => {
          const value = info.getValue();
          if (!value) return "-";
          return formatDateHelper(value);
        },
      },
      {
        header: "Načrtovana vrednost pomoči v €",
        accessorKey: "nacrtovanaVrednost",
        cell: (info) => {
          return formatOptionalAmount(info.getValue());
        },
      },
      {
        header: "Pomoč odobrena",
        accessorKey: "pomocOdobrena",
        enableColumnFilter: true,
        meta: {
          filterVariant: "checkboxFilter",
          filterOptions: [
            { value: true, label: "DA" },
            { value: false, label: "NE" },
          ],
        },
        cell: (info) => {
          const value = info.getValue();
          return value ? "DA" : "NE";
        },
      },
      {
        header: "Datum odobritve pomoči",
        accessorKey: "datumOdobritve",
        enableColumnFilter: true,
        meta: {
          filterVariant: "dateRangeFilter",
        },
        cell: (info) => {
          const value = info.getValue();
          if (!value) return "-";
          return formatDateHelper(value);
        },
      },
      {
        header: "Višina odobrene pomoči €",
        accessorKey: "visinaOdobrenePomoci",
        cell: (info) => {
          return formatOptionalAmount(info.getValue());
        },
      },
    ],
    [filters],
  );

  //handle navigate to details
  const handlePodrobnostiClick = (row) => {
    const id = row.original.id ?? row.original.ID;
    if (id && !Number.isNaN(Number(id))) {
      onOpenDetails(Number(id));
    }
  };

  return (
    <DataGrid
      data={deminimisData?.data || []}
      columns={columns}
      onPaginationChange={setPagination}
      onSortingChange={setSorting}
      sorting={sorting}
      pagination={pagination}
      columnFilters={columnFilters}
      onColumnFiltersChange={setColumnFilters}
      pageCount={deminimisData?.totalPages || 0}
      loading={isLoading}
      searchGlobalPlaceholder="Iskanje po članici, projektu..."
      globalSearch={globalSearch}
      onGlobalSearchChange={setGlobalSearch}
      hasGlobalSearch={true}
      hasActionColumn={true}
      onPodrobnostiActionClick={handlePodrobnostiClick}
      hasExportCsvButton={true}
      exportFileMutation={exportMutation}
    />
  );
};

export default DeminimisNacrtovaneDataGrid;
